<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { CheckCircle2, ChevronDown, ChevronUp, CircleAlert, LoaderCircle } from "lucide-vue-next";

const props = defineProps<{
  running: boolean;
  progress: string;
  steps: Array<{ key: string; label: string; status: "active" | "done" }>;
  readinessScore?: number;
  readyForReview?: boolean;
  missingItems?: string[];
}>();

const expanded = ref(true);
const stagePercent: Record<string, number> = {
  queued: 5,
  context: 14,
  routing: 22,
  researching: 36,
  preparing_response: 48,
  writing: 64,
  structuring: 80,
  validating: 92,
  completed: 100,
};
const currentStep = computed(() => props.steps.find((step) => step.status === "active") || props.steps.at(-1));
const processingPercent = computed(() => {
  if (!props.running && props.steps.length) return 100;
  return stagePercent[currentStep.value?.key || "queued"] || 8;
});
const missing = computed(() => (props.missingItems || []).filter(Boolean).slice(0, 6));
const title = computed(() => {
  if (props.running) return props.progress || "Agent 正在处理";
  if (props.readyForReview) return "完整方案已准备好";
  if (missing.value.length) return `还需要确认 ${missing.value.length} 项`;
  return props.progress || "本轮处理已完成";
});

watch(() => props.running, (running) => {
  if (running) expanded.value = true;
});
</script>

<template>
  <aside class="agent-status" :class="{ expanded, running }" aria-live="polite">
    <button
      class="agent-status__head"
      type="button"
      :title="expanded ? '折叠任务进度' : title"
      :aria-label="expanded ? '折叠任务进度' : `展开任务进度：${title}`"
      @click="expanded = !expanded"
    >
      <span class="state-icon">
        <LoaderCircle v-if="running" :size="17" class="spin" />
        <CheckCircle2 v-else-if="readyForReview" :size="17" />
        <CircleAlert v-else :size="17" />
      </span>
      <span><small>Agent 任务进度</small><b>{{ title }}</b></span>
      <ChevronUp v-if="expanded" :size="17" /><ChevronDown v-else :size="17" />
    </button>
    <div class="agent-status__track"><i :style="{ width: `${processingPercent}%` }"></i></div>
    <div v-if="expanded" class="agent-status__body">
      <div class="metrics">
        <span>本轮处理 <b>{{ processingPercent }}%</b></span>
        <span v-if="readinessScore !== undefined">方案完整度 <b>{{ readinessScore }}%</b></span>
      </div>
      <ol v-if="steps.length" class="steps">
        <li v-for="step in steps" :key="step.key" :class="step.status">
          <CheckCircle2 v-if="step.status === 'done'" :size="13" />
          <LoaderCircle v-else :size="13" class="spin" />
          <span>{{ step.label }}</span>
        </li>
      </ol>
      <section v-if="missing.length" class="missing">
        <b>还需要沟通</b>
        <ul><li v-for="item in missing" :key="item">{{ item }}</li></ul>
      </section>
      <p v-else-if="readyForReview" class="ready">可前往完整方案核对并创建项目草稿。</p>
    </div>
  </aside>
</template>

<style scoped>
.agent-status{position:fixed;right:18px;bottom:20px;z-index:18;width:min(340px,calc(100vw - 28px));overflow:hidden;border:1px solid #bdd5de;background:#fff;box-shadow:0 12px 32px rgba(25,45,55,.16)}
.agent-status__head{display:grid;grid-template-columns:30px minmax(0,1fr) 22px;align-items:center;gap:8px;width:100%;min-height:58px;padding:9px 10px;border:0;background:#f7fbfc;color:var(--ink);text-align:left}.agent-status__head>span:nth-child(2){min-width:0}.agent-status__head small,.agent-status__head b{display:block}.agent-status__head small{color:var(--muted);font-size:10px}.agent-status__head b{margin-top:2px;overflow:hidden;font-size:13px;text-overflow:ellipsis;white-space:nowrap}.state-icon{display:grid;width:28px;height:28px;place-items:center;background:#e8f3f7;color:#2c7d9d}.running .state-icon{color:#227da7}.agent-status__track{height:3px;background:#e7eeee}.agent-status__track i{display:block;height:100%;background:#2c87aa;transition:width .25s ease}.agent-status__body{display:grid;gap:10px;max-height:min(48vh,390px);overflow:auto;padding:10px 12px 12px}.metrics{display:flex;justify-content:space-between;gap:10px;color:var(--muted);font-size:11px}.metrics b{color:var(--ink)}.steps{display:grid;gap:5px;margin:0;padding:0;list-style:none}.steps li{display:grid;grid-template-columns:16px minmax(0,1fr);align-items:start;gap:5px;color:var(--muted);font-size:11px;line-height:1.35}.steps li.done svg{color:var(--success)}.steps li.active{color:#245f78;font-weight:700}.missing{padding:9px;border-left:3px solid #d98b21;background:#fff8ed}.missing>b{font-size:12px}.missing ul{display:grid;gap:4px;margin:6px 0 0;padding-left:17px;color:#725421;font-size:11px;line-height:1.4}.ready{margin:0;padding:8px;background:#edf8f2;color:var(--success);font-size:11px}.spin{animation:spin .9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
@media(max-width:700px){
  .agent-status{right:14px;bottom:calc(74px + env(safe-area-inset-bottom));width:calc(100vw - 28px)}
  .agent-status:not(.expanded){top:50%;right:0;bottom:auto;width:44px;height:44px;border-right:0;border-radius:6px 0 0 6px;transform:translateY(-50%);box-shadow:0 6px 18px rgba(25,45,55,.18)}
  .agent-status:not(.expanded) .agent-status__head{grid-template-columns:1fr;width:44px;min-height:44px;padding:0;place-items:center;background:#f7fbfc}
  .agent-status:not(.expanded) .agent-status__head>span:nth-child(2),
  .agent-status:not(.expanded) .agent-status__head>svg,
  .agent-status:not(.expanded) .agent-status__track{display:none}
  .agent-status:not(.expanded) .state-icon{width:36px;height:36px;background:transparent}
  .agent-status__body{max-height:38vh}
}
@media(prefers-reduced-motion:reduce){.spin{animation:none}.agent-status__track i{transition:none}}
</style>
